(function () {
  'use strict';

  class BedConfigurator {
    constructor(rootEl) {
      this.root = rootEl;
      this.sectionId = rootEl.dataset.sectionId;
      this.formId = rootEl.dataset.formId;
      this.unavailableStyle = rootEl.dataset.unavailableStyle || 'grey_out';
      this.mattressDiscount = parseInt(rootEl.dataset.mattressDiscount, 10) || 10;
      this.mattressHandle = rootEl.dataset.mattressHandle || '';

      // Option indices are 1-based (Shopify forloop.index); 0 means option not present
      this.sizeOptionIndex    = parseInt(rootEl.dataset.sizeIdx, 10) || 0;
      this.storageOptionIndex = parseInt(rootEl.dataset.storageIdx, 10) || 0;
      this.upholsteryOptionIndex = parseInt(rootEl.dataset.upholsteryIdx, 10) || 0;

      // Parse full product JSON from data-product
      let productData = {};
      try { productData = JSON.parse(rootEl.dataset.product); } catch (e) {}
      this.productId    = productData.id || '';
      this.productTitle = productData.title || '';
      this.variants     = productData.variants || [];

      // Hidden form input (lives outside root, in the bcc-native-form)
      this.hiddenInput = null;
      if (this.formId) {
        const form = document.getElementById(this.formId);
        if (form) this.hiddenInput = form.querySelector('input[name="id"]');
      }

      // Mattress modal lives outside root in the page
      this.mattressModal = document.querySelector('[data-mattress-modal]');

      this.variantMap       = {};
      this.basePriceBySize  = {};
      this.headboardVariants = null;

      this.state = {
        selectedSize: '',
        selectedStorage: '',
        selectedUpholstery: '',
        selectedHeadboard: 'none',
        currentVariantId: null,
        currentPrice: 0,
        headboardPrice: 0,
        totalPrice: 0
      };

      this.buildVariantMap();
      this.initFromCurrentVariant();
      this.bindEvents();
      this.updateVariant();
      this.updatePriceDeltas();
      this.updateAvailability();

      // Prefer handle for clean API fetch; fall back to numeric from GID
      this.headboardHandle = rootEl.dataset.headboardHandle || '';
      this.headboardProductGid = rootEl.dataset.headboardGid || '';
      if (this.headboardHandle || this.headboardProductGid) {
        this.loadHeadboardVariants();
      }
    }

    // ── Variant map ──────────────────────────────────────────────────────────

    buildVariantMap() {
      const si = this.sizeOptionIndex;
      const sti = this.storageOptionIndex;
      const ui = this.upholsteryOptionIndex;

      this.variantMap = {};
      this.basePriceBySize = {};

      this.variants.forEach(v => {
        const key = [
          si  ? (v['option' + si]  || '') : '',
          sti ? (v['option' + sti] || '') : '',
          ui  ? (v['option' + ui]  || '') : ''
        ].join('|');
        this.variantMap[key] = v;

        if (si) {
          const size = v['option' + si] || '';
          if (!this.basePriceBySize[size] || v.price < this.basePriceBySize[size]) {
            this.basePriceBySize[size] = v.price;
          }
        }
      });
    }

    findVariant(size, storage, upholstery) {
      const key = [
        this.sizeOptionIndex    ? size      : '',
        this.storageOptionIndex ? storage   : '',
        this.upholsteryOptionIndex ? upholstery : ''
      ].join('|');
      return this.variantMap[key] || null;
    }

    // ── Initialise from current variant (set by Liquid) ───────────────────────

    initFromCurrentVariant() {
      const params = new URLSearchParams(window.location.search);
      const urlVariantId = params.get('variant');

      let initVariant = null;
      if (urlVariantId) {
        initVariant = this.variants.find(v => String(v.id) === urlVariantId);
      }
      if (!initVariant) {
        const currentId = parseInt(this.root.dataset.currentVariant, 10);
        initVariant = this.variants.find(v => v.id === currentId);
      }
      if (!initVariant) {
        initVariant = this.variants.find(v => v.available) || this.variants[0];
      }

      if (initVariant) {
        const si  = this.sizeOptionIndex;
        const sti = this.storageOptionIndex;
        const ui  = this.upholsteryOptionIndex;

        this.state.selectedSize        = si  ? (initVariant['option' + si]  || '') : '';
        this.state.selectedStorage     = sti ? (initVariant['option' + sti] || '') : '';
        this.state.selectedUpholstery  = ui  ? (initVariant['option' + ui]  || '') : '';
        this.state.currentVariantId    = initVariant.id;
        this.state.currentPrice        = initVariant.price;
        this.state.totalPrice          = initVariant.price;

        this.syncUISelections();
      }
    }

    // ── Events ───────────────────────────────────────────────────────────────

    bindEvents() {
      // Tab switching
      this.root.querySelectorAll('.bcc-filter-tab').forEach(tab => {
        tab.addEventListener('click', () => this.setActiveTab(tab.dataset.tab));
      });

      // Option buttons — all share [data-option] / [data-value]
      this.root.querySelectorAll('[data-option]').forEach(btn => {
        btn.addEventListener('click', () => {
          const opt = btn.dataset.option;
          const val = btn.dataset.value;
          if (opt === 'upholstery') this.selectUpholstery(val);
          else if (opt === 'size')        this.selectSize(val);
          else if (opt === 'storage')     this.selectStorage(val);
          else if (opt === 'headboard')   this.selectHeadboard(val);
        });
      });

      // Add to Cart
      const atcBtn = this.root.querySelector('[data-atc]');
      if (atcBtn) {
        atcBtn.addEventListener('click', () => this.handleATC());
      }

      // Mattress prompt link (inside root)
      const openMattress = this.root.querySelector('[data-open-mattress]');
      if (openMattress) {
        openMattress.addEventListener('click', () => this.showMattressModal());
      }

      // Mattress modal close buttons (modal is outside root)
      if (this.mattressModal) {
        this.mattressModal.querySelectorAll('[data-close-mattress]').forEach(el => {
          el.addEventListener('click', () => this.hideMattressModal());
        });
        this.mattressModal.addEventListener('keydown', e => {
          if (e.key === 'Escape') this.hideMattressModal();
        });
      }
    }

    // ── Tab panel switching ───────────────────────────────────────────────────

    setActiveTab(tabName) {
      this.root.querySelectorAll('.bcc-filter-tab').forEach(tab => {
        const isActive = tab.dataset.tab === tabName;
        tab.classList.toggle('bcc-filter-tab--active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
      });

      this.root.querySelectorAll('.bcc-panel').forEach(panel => {
        // panel id format: bcc-panel-colour, bcc-panel-size, etc.
        const name = panel.id.replace(/^bcc-panel-/, '');
        const isActive = name === tabName;
        panel.classList.toggle('bcc-panel--active', isActive);
      });
    }

    // ── Option selection ──────────────────────────────────────────────────────

    selectUpholstery(value) {
      this.state.selectedUpholstery = value;
      this.root.querySelectorAll('[data-option="upholstery"]').forEach(el => {
        el.classList.toggle('bcc-swatch--selected', el.dataset.value === value);
      });
      this.updateVariant();
      this.updateAvailability();
    }

    selectSize(value) {
      this.state.selectedSize = value;
      this.root.querySelectorAll('[data-option="size"]').forEach(el => {
        el.classList.toggle('bcc-option--selected', el.dataset.value === value);
      });
      this.updateVariant();
      this.updatePriceDeltas();
      this.updateAvailability();
    }

    selectStorage(value) {
      this.state.selectedStorage = value;
      this.root.querySelectorAll('[data-option="storage"]').forEach(el => {
        el.classList.toggle('bcc-option--selected', el.dataset.value === value);
      });
      this.updateVariant();
      this.updatePriceDeltas();
      this.updateAvailability();
    }

    selectHeadboard(value) {
      this.state.selectedHeadboard = value;
      // Headboard price: if a headboard variant is loaded and selected, add its price
      if (value !== 'none' && this.headboardVariants) {
        const hbVariant = this.findHeadboardVariant();
        this.state.headboardPrice = hbVariant ? hbVariant.price : 0;
      } else {
        this.state.headboardPrice = 0;
      }

      this.root.querySelectorAll('[data-option="headboard"]').forEach(el => {
        el.classList.toggle('bcc-option--selected', el.dataset.value === value);
      });

      this.updateTotalPrice();
      this.updateTabValues();
      this.updateSummary();
    }

    // ── Sync UI to current state ──────────────────────────────────────────────

    syncUISelections() {
      // Swatches
      this.root.querySelectorAll('[data-option="upholstery"]').forEach(el => {
        el.classList.toggle('bcc-swatch--selected', el.dataset.value === this.state.selectedUpholstery);
      });
      // Size
      this.root.querySelectorAll('[data-option="size"]').forEach(el => {
        el.classList.toggle('bcc-option--selected', el.dataset.value === this.state.selectedSize);
      });
      // Storage
      this.root.querySelectorAll('[data-option="storage"]').forEach(el => {
        el.classList.toggle('bcc-option--selected', el.dataset.value === this.state.selectedStorage);
      });
      // Headboard
      this.root.querySelectorAll('[data-option="headboard"]').forEach(el => {
        el.classList.toggle('bcc-option--selected', el.dataset.value === this.state.selectedHeadboard);
      });

      this.updateTabValues();
      this.updateSummary();
    }

    // ── Variant lookup & price ────────────────────────────────────────────────

    updateVariant() {
      const variant = this.findVariant(
        this.state.selectedSize,
        this.state.selectedStorage,
        this.state.selectedUpholstery
      );

      if (variant) {
        this.state.currentVariantId = variant.id;
        this.state.currentPrice = variant.price;

        // Update hidden form input so Shopify buy form stays in sync
        if (this.hiddenInput) {
          this.hiddenInput.value = String(variant.id);
          this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Update URL without reloading
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}

        // Notify Pinnacle theme of variant change (for gallery, price, etc.)
        this.dispatchVariantChange(variant);
      }

      // Update ATC button state
      const atcBtn = this.root.querySelector('[data-atc]');
      if (atcBtn) {
        const available = variant ? variant.available : false;
        atcBtn.disabled = !available;
        const textEl = atcBtn.querySelector('.bcc-atc-text');
        if (textEl) textEl.textContent = available ? 'Add to Cart' : 'Sold Out';
      }

      this.updateTotalPrice();
      this.updateTabValues();
      this.updateSummary();
    }

    dispatchVariantChange(variant) {
      // Pinnacle / Dawn compatible events
      document.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant, sectionId: this.sectionId }
      }));
      document.dispatchEvent(new CustomEvent('product:variant-change', {
        bubbles: true,
        detail: { variant, sectionId: this.sectionId }
      }));
      if (variant.featured_image) {
        document.dispatchEvent(new CustomEvent('variant:media-change', {
          bubbles: true,
          detail: { variant }
        }));
      }
    }

    updateTotalPrice() {
      this.state.totalPrice = this.state.currentPrice + this.state.headboardPrice;
      const priceEls = this.root.querySelectorAll('[data-total-price]');
      priceEls.forEach(el => {
        el.textContent = this.formatMoney(this.state.totalPrice);
      });
    }

    updatePriceDeltas() {
      // ── Storage deltas ────────────────────────────────────────────────────
      // Compare each storage option vs the no-storage (cheapest) option for the current size
      const storageBasePrice = this.basePriceBySize[this.state.selectedSize] || 0;

      this.root.querySelectorAll('[data-option="storage"]').forEach(btn => {
        const storageVal = btn.dataset.value;
        const deltaEl = btn.querySelector('.bcc-option-delta');
        if (!deltaEl) return;

        const testVariant = this.findVariant(
          this.state.selectedSize,
          storageVal,
          this.state.selectedUpholstery
        );
        if (testVariant) {
          const delta = testVariant.price - storageBasePrice;
          deltaEl.textContent = delta > 0 ? '+' + this.formatMoney(delta) : '';
        }
      });

      // ── Size deltas ───────────────────────────────────────────────────────
      // Find the cheapest size price with the current storage & upholstery combo,
      // then show how each size differs from that base
      let minSizePrice = Infinity;
      this.root.querySelectorAll('[data-option="size"]').forEach(btn => {
        const v = this.findVariant(btn.dataset.value, this.state.selectedStorage, this.state.selectedUpholstery);
        if (v && v.price < minSizePrice) minSizePrice = v.price;
      });
      if (!isFinite(minSizePrice)) minSizePrice = 0;

      this.root.querySelectorAll('[data-option="size"]').forEach(btn => {
        const deltaEl = btn.querySelector('.bcc-option-delta');
        if (!deltaEl) return;
        const v = this.findVariant(btn.dataset.value, this.state.selectedStorage, this.state.selectedUpholstery);
        if (v) {
          const delta = v.price - minSizePrice;
          if (delta > 0) {
            deltaEl.textContent = '+' + this.formatMoney(delta);
          } else if (delta < 0) {
            deltaEl.textContent = this.formatMoney(delta); // shows negative
          } else {
            deltaEl.textContent = ''; // cheapest — no delta shown
          }
        }
      });
    }

    // ── Availability ──────────────────────────────────────────────────────────

    updateAvailability() {
      const si  = this.sizeOptionIndex;
      const sti = this.storageOptionIndex;
      const ui  = this.upholsteryOptionIndex;

      // Size options
      this.root.querySelectorAll('[data-option="size"]').forEach(el => {
        const val = el.dataset.value;
        const available = this.variants.some(v =>
          (!si  || v['option' + si]  === val) &&
          (!sti || !this.state.selectedStorage   || v['option' + sti] === this.state.selectedStorage) &&
          (!ui  || !this.state.selectedUpholstery || v['option' + ui]  === this.state.selectedUpholstery) &&
          v.available
        );
        this.setAvailabilityState(el, available, false);
      });

      // Storage options
      this.root.querySelectorAll('[data-option="storage"]').forEach(el => {
        const val = el.dataset.value;
        const available = this.variants.some(v =>
          (!si  || !this.state.selectedSize       || v['option' + si]  === this.state.selectedSize) &&
          (!sti || v['option' + sti] === val) &&
          (!ui  || !this.state.selectedUpholstery || v['option' + ui]  === this.state.selectedUpholstery) &&
          v.available
        );
        this.setAvailabilityState(el, available, false);
      });

      // Swatches
      this.root.querySelectorAll('[data-option="upholstery"]').forEach(el => {
        const val = el.dataset.value;
        const available = this.variants.some(v =>
          (!si  || !this.state.selectedSize    || v['option' + si]  === this.state.selectedSize) &&
          (!sti || !this.state.selectedStorage || v['option' + sti] === this.state.selectedStorage) &&
          (!ui  || v['option' + ui] === val) &&
          v.available
        );
        this.setAvailabilityState(el, available, true);
      });
    }

    setAvailabilityState(el, available, isSwatch) {
      const unavailableClass = isSwatch ? 'bcc-swatch--unavailable' : 'bcc-option--unavailable';

      if (available) {
        el.classList.remove(unavailableClass);
        el.removeAttribute('disabled');
        if (this.unavailableStyle === 'hide') el.style.display = '';
      } else {
        if (this.unavailableStyle === 'hide') {
          el.style.display = 'none';
        } else {
          el.classList.add(unavailableClass);
        }
        el.setAttribute('disabled', 'disabled');
      }
    }

    // ── UI label updates ──────────────────────────────────────────────────────

    updateTabValues() {
      const set = (name, val) => {
        const el = this.root.querySelector(`[data-tab-val="${name}"]`);
        if (el) el.textContent = val || '—';
      };
      set('colour', this.state.selectedUpholstery);
      set('size', this.state.selectedSize);
      set('storage', this.state.selectedStorage);
      set('headboard', this.state.selectedHeadboard === 'none' ? 'None' : this.state.selectedHeadboard);
    }

    updateSummary() {
      const set = (key, val) => {
        const el = this.root.querySelector(`[data-chip="${key}"] .bcc-chip-value`);
        if (el) el.textContent = val || '—';
      };
      set('upholstery', this.state.selectedUpholstery);
      set('size', this.state.selectedSize);
      set('storage', this.state.selectedStorage);
      set('headboard', this.state.selectedHeadboard === 'none' ? 'None' : this.state.selectedHeadboard);
    }

    // ── Add to Cart ───────────────────────────────────────────────────────────

    async handleATC() {
      if (!this.state.currentVariantId) {
        this.shakeTab('colour');
        return;
      }

      const btn = this.root.querySelector('[data-atc]');
      if (!btn || btn.disabled) return;

      const textEl    = btn.querySelector('.bcc-atc-text');
      const spinnerEl = btn.querySelector('.bcc-atc-spinner');

      btn.classList.add('bcc-atc-button--loading');
      btn.disabled = true;
      if (textEl)    textEl.style.opacity  = '0';
      if (spinnerEl) spinnerEl.style.display = 'inline-block';

      const items = [{
        id: this.state.currentVariantId,
        quantity: 1,
        properties: {
          'Headboard': this.state.selectedHeadboard === 'none' ? 'No Headboard' : this.state.selectedHeadboard
        }
      }];

      // Add headboard as separate line item if selected and variant loaded
      if (this.state.selectedHeadboard !== 'none' && this.headboardVariants) {
        const hbVariant = this.findHeadboardVariant();
        if (hbVariant) {
          items.push({
            id: hbVariant.id,
            quantity: 1,
            properties: {
              'For': this.productTitle,
              'Size': this.state.selectedSize
            }
          });
        }
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        const data = await response.json();

        if (data.status) {
          this.showError(data.description || data.message || 'Could not add to cart.');
          return;
        }

        // Notify theme cart
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          bubbles: true,
          detail: { product: data.items ? data.items[0] : data }
        }));

        if (this.mattressHandle) {
          this.showMattressModal();
        } else {
          this.openCartDrawer();
        }

      } catch (e) {
        this.showError('Something went wrong. Please try again.');
      } finally {
        btn.classList.remove('bcc-atc-button--loading');
        btn.disabled = false;
        if (textEl)    textEl.style.opacity  = '';
        if (spinnerEl) spinnerEl.style.display = 'none';
      }
    }

    showError(msg) {
      const errEl = this.root.querySelector('.bcc-atc-error');
      if (errEl) {
        errEl.textContent = msg;
        setTimeout(() => { errEl.textContent = ''; }, 5000);
      }
    }

    shakeTab(tabName) {
      const tab = this.root.querySelector(`[data-tab="${tabName}"]`);
      if (tab) {
        tab.classList.add('bcc-filter-tab--shake');
        setTimeout(() => tab.classList.remove('bcc-filter-tab--shake'), 600);
        this.setActiveTab(tabName);
      }
    }

    openCartDrawer() {
      // Try Pinnacle/Dawn cart drawer events
      document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    }

    // ── Mattress modal ────────────────────────────────────────────────────────

    showMattressModal() {
      if (!this.mattressModal) return;
      this.mattressModal.setAttribute('aria-hidden', 'false');
      this.mattressModal.classList.add('bcc-modal--open');
      document.body.style.overflow = 'hidden';
      this.loadMattressProducts();
    }

    hideMattressModal() {
      if (!this.mattressModal) return;
      this.mattressModal.setAttribute('aria-hidden', 'true');
      this.mattressModal.classList.remove('bcc-modal--open');
      document.body.style.overflow = '';
      this.openCartDrawer();
    }

    async loadMattressProducts() {
      const grid = this.mattressModal
        ? this.mattressModal.querySelector('[data-mattress-products]')
        : null;
      if (!grid || !this.mattressHandle) return;

      grid.innerHTML = '<div class="bcc-skeleton bcc-skeleton--product"></div><div class="bcc-skeleton bcc-skeleton--product"></div>';

      try {
        const resp = await fetch(`/collections/${this.mattressHandle}/products.json?limit=12`);
        const data = await resp.json();

        if (!data.products || data.products.length === 0) {
          grid.innerHTML = '<p style="padding:16px">No mattresses available.</p>';
          return;
        }

        const sizeStr = this.state.selectedSize.toLowerCase();
        let html = '';

        data.products.forEach(product => {
          const matchingVariants = product.variants.filter(v => {
            const t = (v.title || '').toLowerCase();
            const o1 = (v.option1 || '').toLowerCase();
            const o2 = (v.option2 || '').toLowerCase();
            return t.includes(sizeStr) || o1.includes(sizeStr) || o2.includes(sizeStr);
          });

          const variant = matchingVariants[0] || product.variants[0];
          if (!variant) return;

          const orig       = parseInt(variant.price, 10);
          const discounted = Math.round(orig * (100 - this.mattressDiscount) / 100);
          const img        = product.images && product.images[0] ? product.images[0].src : '';

          html += `
            <div class="bcc-mattress-card">
              ${img ? `<img src="${img}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
              <div class="bcc-mattress-info">
                <p class="bcc-mattress-title">${this.escapeHtml(product.title)}</p>
                <p class="bcc-mattress-price">
                  <span class="bcc-mattress-orig">${this.formatMoney(orig)}</span>
                  <strong>${this.formatMoney(discounted)}</strong>
                </p>
              </div>
              <button type="button" class="bcc-mattress-add button" data-mattress-variant-id="${variant.id}">Add</button>
            </div>`;
        });

        grid.innerHTML = html || '<p style="padding:16px">No mattresses found for this size.</p>';

        grid.querySelectorAll('[data-mattress-variant-id]').forEach(btn => {
          btn.addEventListener('click', () => {
            this.handleMattressAdd(parseInt(btn.dataset.mattressVariantId, 10));
          });
        });
      } catch (e) {
        grid.innerHTML = '<p style="padding:16px">Could not load mattresses.</p>';
      }
    }

    async handleMattressAdd(variantId) {
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        this.hideMattressModal();
      } catch (e) {
        // Silent fail — modal will still close
        this.hideMattressModal();
      }
    }

    // ── Headboard loading ─────────────────────────────────────────────────────

    async loadHeadboardVariants() {
      // Prefer handle (cleanest API call); fall back to searching all products by tag
      const handle = this.headboardHandle;
      const numericId = String(this.headboardProductGid || '').replace(/\D/g, '');

      try {
        if (handle) {
          // Direct product fetch by handle — most reliable
          const resp = await fetch(`/products/${handle}.json`);
          if (resp.ok) {
            const data = await resp.json();
            if (data.product && data.product.variants) {
              this.headboardVariants = data.product.variants;
              this.renderHeadboardOptions();
              return;
            }
          }
        }

        // Fallback: scan products tagged 'headboard-addon' or match by numeric ID
        const allResp = await fetch('/products.json?limit=250');
        if (!allResp.ok) return;
        const allData = await allResp.json();
        const hbProduct = allData.products.find(p =>
          (numericId && String(p.id) === numericId) ||
          (p.tags && (
            p.tags.includes('headboard-addon') ||
            p.tags.includes('headboard_addon') ||
            p.tags.includes('headboard')
          ))
        );
        if (hbProduct) {
          this.headboardVariants = hbProduct.variants;
          this.renderHeadboardOptions();
        }
      } catch (e) {
        // Headboard loading failed — only "No Headboard" shown
      }
    }

    renderHeadboardOptions() {
      const container = this.root.querySelector('[data-headboard-options]');
      if (!container || !this.headboardVariants) return;

      let html = '';
      this.headboardVariants.forEach(v => {
        const price = v.price > 0 ? ' +' + this.formatMoney(v.price) : '';
        html += `
          <button class="bcc-option" data-option="headboard" data-value="${this.escapeHtml(v.title)}">
            <span class="bcc-option-icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="4" y="4" width="24" height="12" rx="2"/>
                <path d="M6 16v12M26 16v12M4 22h24"/>
              </svg>
            </span>
            <span class="bcc-option-label">${this.escapeHtml(v.title)}${price}</span>
          </button>`;
      });

      container.innerHTML = html;

      // Re-bind events for new headboard buttons
      container.querySelectorAll('[data-option="headboard"]').forEach(btn => {
        btn.addEventListener('click', () => this.selectHeadboard(btn.dataset.value));
      });
    }

    findHeadboardVariant() {
      if (!this.headboardVariants) return null;
      const sizeStr = this.state.selectedSize.toLowerCase();
      return this.headboardVariants.find(v => {
        const t   = (v.title  || '').toLowerCase();
        const o1  = (v.option1 || '').toLowerCase();
        return t.includes(sizeStr) || o1.includes(sizeStr);
      }) || this.headboardVariants[0];
    }

    // ── Utilities ─────────────────────────────────────────────────────────────

    formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney) {
        const fmt = window.Shopify.moneyFormat || '£{{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
      return '£' + (cents / 100).toFixed(2);
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = String(str);
      return div.innerHTML;
    }
  }

  // ── Bootstrap ───────────────────────────────────────────────────────────────

  function init() {
    // Find the section root — id starts with "BedConfigurator-"
    const root = document.querySelector('[id^="BedConfigurator-"]');
    if (!root) return;

    // Teleport into product info right-column (Pinnacle theme)
    const target = document.querySelector('.product-information--inner');
    if (target) {
      // Insert where the native variant picker sits (we hide it with CSS).
      // This puts the configurator immediately after the price/title block.
      const anchor =
        target.querySelector('.variations') ||
        target.querySelector('product-form') ||
        target.querySelector('.product-form') ||
        target.querySelector('.product-add-to-cart-container');

      if (anchor) {
        target.insertBefore(root, anchor);
      } else {
        // Fallback: insert after the price element if we can find it
        const priceEl = target.querySelector('.price, .price__container, .price-wrapper, .product__price');
        if (priceEl) {
          priceEl.after(root);
        } else {
          // Last resort: prepend so it appears near the top
          target.prepend(root);
        }
      }
    }

    // Make visible (section renders with display:none to prevent flash)
    root.style.display = '';

    new BedConfigurator(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
